import { BaseState } from './BaseState';
import { Subject, BehaviorSubject } from 'rxjs';
import { IObjectPropWindowCallType } from '../types';

/**
 * Manages UI-related state and interactions
 */
export class UIState extends BaseState {
  // RxJS subjects for reactive state management
  public mode$ = new BehaviorSubject<string>("none");
  public realScale$ = new BehaviorSubject<number | null>(null);
  public stamp$ = new BehaviorSubject<boolean>(true);
  public showBlockSearch$ = new BehaviorSubject<boolean>(false);
  public showAlertDialog$ = new BehaviorSubject<any>(null);
  public showErrorDialog$ = new BehaviorSubject<any>(null);
  public showInputBlockCollectionDialog$ = new BehaviorSubject<any>(null);
  public addToBlockCollection$ = new Subject<any>();
  public updateUI$ = new Subject<any>();
  public commandsUI$ = new BehaviorSubject<any>({
    callNetworkInfoWindow: { show: false, calledWithShortcut: false },
    focusFlowRateInput: { show: false, calledWithShortcut: false }
  });

  private state = {
    // Mode and cursor
    mode: "none",
    realScale: null as number | null,
    
    // UI visibility states
    isPrintAreaVisible: false,
    isStampPdfVisible: true,
    blockUI: false,
    
    // Dialog states
    showBlockSearch: false,
    showAlertDialog: false,
    showErrorDialog: false,
    showInputBlockCollectionDialog: false,
    
    // Dialog data
    alertDialog: {
      title: '',
      message: '',
      timeout: 2000,
      isVisible: false
    },
    errorDialog: {
      title: '',
      message: '',
      isVisible: false
    },
    inputBlockCollectionDialog: {
      title: '',
      message: '',
      value: '',
      isVisible: false
    },
    
    // Commands UI
    commandsUI: {
      callNetworkInfoWindow: {
        show: false,
        calledWithShortcut: false
      },
      focusFlowRateInput: {
        show: false,
        calledWithShortcut: false
      }
    },
    
    // Event listeners
    eventListeners: [] as Array<{elem: any, type: string, func: any}>,
  };

  constructor() {
    super();
  }

  getState() {
    return { ...this.state };
  }

  updateState(updates: Partial<typeof this.state>) {
    this.state = { ...this.state, ...updates };
    this.emitStateChange(this.state);
  }

  // Mode management
  getMode(): string {
    return this.state.mode;
  }

  setMode(mode: string) {
    this.state.mode = mode;
    this.mode$.next(mode);
  }

  // Scale management
  getRealScale(): number | null {
    return this.state.realScale;
  }

  setRealScale(scale: number | null) {
    this.state.realScale = scale;
    this.realScale$.next(scale);
  }

  // Dialog management
  showAlertDialog(title: string, message: string, timeout: number = 2000) {
    this.state.alertDialog = {
      title,
      message,
      timeout,
      isVisible: true
    };
    this.showAlertDialog$.next(this.state.alertDialog);
  }

  hideAlertDialog() {
    this.state.alertDialog.isVisible = false;
    this.showAlertDialog$.next(this.state.alertDialog);
  }

  showErrorDialog(title: string, message: string) {
    this.state.errorDialog = {
      title,
      message,
      isVisible: true
    };
    this.showErrorDialog$.next(this.state.errorDialog);
  }

  hideErrorDialog() {
    this.state.errorDialog.isVisible = false;
    this.showErrorDialog$.next(this.state.errorDialog);
  }

  showInputBlockCollectionDialog(title: string, message: string, value: string = '') {
    this.state.inputBlockCollectionDialog = {
      title,
      message,
      value,
      isVisible: true
    };
    this.showInputBlockCollectionDialog$.next(this.state.inputBlockCollectionDialog);
  }

  hideInputBlockCollectionDialog() {
    this.state.inputBlockCollectionDialog.isVisible = false;
    this.showInputBlockCollectionDialog$.next(this.state.inputBlockCollectionDialog);
  }

  showBlockSearch() {
    this.state.showBlockSearch = true;
    this.showBlockSearch$.next(true);
  }

  hideBlockSearch() {
    this.state.showBlockSearch = false;
    this.showBlockSearch$.next(false);
  }

  // Commands UI
  setNetworkInfoWindow(show: boolean, calledWithShortcut: boolean = false) {
    this.state.commandsUI.callNetworkInfoWindow = { show, calledWithShortcut };
    this.commandsUI$.next(this.state.commandsUI);
  }

  setFocusFlowRateInput(show: boolean, calledWithShortcut: boolean = false) {
    this.state.commandsUI.focusFlowRateInput = { show, calledWithShortcut };
    this.commandsUI$.next(this.state.commandsUI);
  }

  // Event listener management
  addEventListener(elem: any, type: string, func: any) {
    const listener = { elem, type, func };
    this.state.eventListeners.push(listener);
    this.emitStateChange({ eventListeners: this.state.eventListeners });
  }

  removeAllEventListeners() {
    this.state.eventListeners.forEach(listener => {
      listener.elem.removeEventListener(listener.type, listener.func);
    });
    this.state.eventListeners = [];
    this.emitStateChange({ eventListeners: this.state.eventListeners });
  }

  // UI state getters
  isBlockUI(): boolean {
    return this.state.blockUI;
  }

  setBlockUI(blocked: boolean) {
    this.state.blockUI = blocked;
    this.updateUI$.next({ blockUI: blocked });
  }

  isStampPdfVisible(): boolean {
    return this.state.isStampPdfVisible;
  }

  setStampPdfVisible(visible: boolean) {
    this.state.isStampPdfVisible = visible;
    this.stamp$.next(visible);
  }

  isPrintAreaVisible(): boolean {
    return this.state.isPrintAreaVisible;
  }

  setPrintAreaVisible(visible: boolean) {
    this.state.isPrintAreaVisible = visible;
    this.updateUI$.next({ isPrintAreaVisible: visible });
  }
}
